import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { adminRoleOptions } from '@constants/admins.constant';
import { EAdminInviteMode } from '@enums/admin.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  createAdminSchema,
  type CreateAdminFormInput,
  type CreateAdminPayload,
} from '@services/admins/dtos/create.dto';
import { adminService } from '@services/admins/service';
import type { CreateAdminOutput } from '@services/admins/types/create.type';

import { updateCreatedAdminCache } from './@helpers/updateCreatedAdminCache.helper';

export const Route = createFileRoute('/_layout/(private)/admins/create')({
  component: AdminCreate,
});

function AdminCreate() {
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext();

  const form = useForm<CreateAdminFormInput, unknown, CreateAdminPayload>({
    resolver: zodResolver(createAdminSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      firstName: '',
      inviteMode: EAdminInviteMode.SSO,
      lastName: '',
      phone: '',
    },
  });

  const { isPending, mutate: createAdmin } = useMutation<
    CreateAdminOutput,
    unknown,
    CreateAdminPayload
  >({
    mutationFn: adminService.create,
    onError: handleRequestError,
    onSuccess: async ({ admin }) => {
      await updateCreatedAdminCache({ admin, queryClient });
      navigate({
        replace: true,
        to: '/admins',
      });
      toast.success('Success', {
        description: 'Admin created successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createAdmin(values);
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Create admin" to="/admins" />

      <form className="mx-auto mt-5 space-y-2 px-4" onSubmit={handleSubmit}>
        <div className="grid gap-2.5 sm:grid-cols-2">
          <InputControlled label="First name" name="firstName" />
          <InputControlled label="Last name" name="lastName" />
          <InputControlled label="Email" name="email" type="email" />
          <InputControlled label="Phone" name="phone" variant="phone" />
          <InputControlled
            label="Role"
            name="role"
            options={adminRoleOptions}
            placeholder="Select a role"
            variant="select"
          />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            rounded="lg"
            size="lg"
            to="/admins"
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
